/**
 * App icon badge service.
 *
 * WHY THIS EXISTS. An iOS badge is not self-managing. Whatever number an APNs payload
 * puts on the icon stays there until something explicitly sets a different one —
 * reading the notification, swiping it away, or clearing Notification Center does
 * nothing to it. So the badge needs writing from BOTH directions: the server sends the
 * recipient's live unread count with every push (so the icon is right even when the app
 * is not running), and the app re-asserts that same count whenever it changes in-app,
 * on resume, and on logout. This module is the client half.
 *
 * ALWAYS SET, NEVER DECREMENT. Every entry point takes an absolute count. A
 * decrement-on-read scheme drifts the moment one write is missed — and on iOS a missed
 * write is permanent, because nothing ever corrects it.
 *
 * PERMISSION. iOS treats the badge as its own authorization option, separate from
 * alerts, and `set` fails silently without it. Capacitor's push plugin already requests
 * `[.alert, .sound, .badge]` together in `requestAuthorization`
 * (@capacitor/push-notifications PushNotificationsHandler.swift), so granting
 * notifications grants the badge — there is no second prompt to add.
 *
 * NOTHING HERE THROWS. A badge is cosmetic; it must never be able to break a render, a
 * mutation, or a logout.
 */

import { Badge } from '@capawesome/capacitor-badge';
import { isNative } from '@/utils/platform';

/**
 * Navigator Badging (web/PWA). Checked by hand rather than through the plugin's web
 * implementation, which calls `navigator.setAppBadge` unconditionally — including from
 * its own constructor — and throws on every browser that lacks it.
 */
function hasWebBadging(): boolean {
  return typeof navigator !== 'undefined' && 'setAppBadge' in navigator;
}

/** Whether this platform can show a count on the app icon at all. */
export function isAppBadgeSupported(): boolean {
  return isNative || hasWebBadging();
}

/** Normalise to something the platforms accept: a non-negative integer, or null. */
function normalizeCount(count: number): number | null {
  if (!Number.isFinite(count)) return null;
  const rounded = Math.trunc(count);
  return rounded < 0 ? 0 : rounded;
}

/**
 * Write an absolute count onto the app icon.
 *
 * A count of 0 removes the badge. On iOS `Badge.set({ count: 0 })` also clears delivered
 * notifications, which is exactly what we want: with nothing unread, a banner still
 * sitting in Notification Center is stale and can only cause confusion.
 */
export async function setAppBadge(count: number): Promise<void> {
  const normalized = normalizeCount(count);
  if (normalized === null) return;

  try {
    if (isNative) {
      await Badge.set({ count: normalized });
      return;
    }

    if (!hasWebBadging()) return;

    if (normalized === 0) {
      await navigator.clearAppBadge();
    } else {
      await navigator.setAppBadge(normalized);
    }
  } catch (error) {
    console.warn('[Badge] Failed to set app badge:', error);
  }
}

/** Remove the badge entirely. Used on logout and account switch. */
export async function clearAppBadge(): Promise<void> {
  try {
    if (isNative) {
      await Badge.clear();
      return;
    }

    if (!hasWebBadging()) return;
    await navigator.clearAppBadge();
  } catch (error) {
    console.warn('[Badge] Failed to clear app badge:', error);
  }
}
